import { BackIcon, ForwardIcon } from '@assets/icons';
import { Button } from '@components/button';
import {
  useGetWorkspaceResourcesAvailableVolumesQuery,
  useGetWorkspaceResourcesWorkspaceVolumesQuery,
} from '@generated/graphql';
import { Grid, Stack } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ResourcesType, TransferList, intersection, not } from './TransferList';

interface VolumesTransferListProps {
  organizationId: string;
  workspaceId: string;
  selectedVolumes: ResourcesType;
  setSelectedVolumes: Dispatch<SetStateAction<ResourcesType>>;
  setVolumesDirty: Dispatch<SetStateAction<boolean>>;
}

const getVolumes = (
  volumes: { volumeId: string; name: string }[],
  selected: string[],
  available?: boolean,
) => {
  return (volumes ?? [])
    .filter(({ volumeId }) =>
      available ? !selected.includes(volumeId) : selected.includes(volumeId),
    )
    .map(({ volumeId, name }) => ({ id: volumeId, name }));
};

export const VolumesTransferList = ({
  organizationId,
  workspaceId,
  selectedVolumes,
  setSelectedVolumes,
  setVolumesDirty,
}: VolumesTransferListProps) => {
  const [checked, setChecked] = useState<ResourcesType>([]);
  const [right, setRight] = useState<ResourcesType>([]);

  const { data: availableVolumes, loading: availableVolumesLoading } =
    useGetWorkspaceResourcesAvailableVolumesQuery({
      variables: {
        organizationId,
      },
    });

  const { data: includedVolumes, loading: includedVolumesLoading } =
    useGetWorkspaceResourcesWorkspaceVolumesQuery({
      variables: {
        workspaceId,
      },
    });
  const selectedVolumesIds = (includedVolumes?.getVolumes ?? []).map((v) => v.volumeId);

  useEffect(() => {
    if (!availableVolumesLoading && !includedVolumesLoading) {
      setRight(getVolumes(availableVolumes?.getVolumes, selectedVolumesIds, true));
      setSelectedVolumes(getVolumes(availableVolumes?.getVolumes, selectedVolumesIds, false));
    }

    return () => {
      setRight([]);
      setSelectedVolumes([]);
    };
    // Init-once when both loadings flip false; deliberately omits data deps
    // to avoid overwriting user edits on background refetches. WorkspaceResourcesModal
    // is slated for rework — revisit this pattern then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableVolumesLoading, includedVolumesLoading]);

  const leftChecked = intersection(checked, selectedVolumes);
  const rightChecked = intersection(checked, right);

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setSelectedVolumes(not(selectedVolumes, leftChecked));
    setVolumesDirty(true);
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setSelectedVolumes(selectedVolumes.concat(rightChecked));
    setRight(not(right, rightChecked));
    setVolumesDirty(true);
    setChecked(not(checked, rightChecked));
  };
  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item xs={5}>
        <TransferList
          title="Included"
          items={selectedVolumes}
          checked={checked}
          setChecked={setChecked}
        />
      </Grid>
      <Grid item xs={2}>
        <Grid container direction="column" alignItems="center">
          <Stack spacing="16px">
            <Button
              variant="secondary"
              onClick={handleCheckedLeft}
              data-cy="Volumes-Transfer-List-Add-Button"
              startIcon={BackIcon}
              disabled={rightChecked.length === 0}
            >
              Add
            </Button>
            <Button
              variant="secondary"
              onClick={handleCheckedRight}
              data-cy="Volumes-Transfer-List-Remove-Button"
              disabled={leftChecked.length === 0}
              endIcon={ForwardIcon}
            >
              Remove
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Grid item xs={5}>
        <TransferList title="Excluded" items={right} checked={checked} setChecked={setChecked} />
      </Grid>
    </Grid>
  );
};
